import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { DensityToggle } from '../../components/DensityToggle';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { useSession } from '../auth/queries';
import type { CustomerStatus } from './api';
import { CustomerDialog } from './CustomerDialog';
import { useCustomerList } from './queries';

const STATUSES: readonly CustomerStatus[] = ['prospect', 'active', 'inactive'];

/**
 * Die Kundenliste -- F2-01, K-137.
 *
 * Der Einstieg ins CRM. Gesucht wird ueber Name und Kundennummer; das ist die
 * Frage, die hier gestellt wird ("wie hiess der noch"). Ein Volltextindex
 * ueber Notizen und Anschriften waere mehr Werkzeug, als die Frage braucht --
 * und bliebe die meiste Zeit ungenutzt.
 */
export function CustomerListPage() {
  const { t } = useTranslation();
  const session = useSession();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<CustomerStatus | undefined>(undefined);
  const [onlyVip, setOnlyVip] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const query = useCustomerList({
    ...(search ? { q: search } : {}),
    ...(status ? { status } : {}),
    ...(onlyVip ? { is_vip: true } : {}),
  });
  const customers = query.data?.pages.flatMap((page) => page.items) ?? [];

  const canWrite = (session.data?.permissions ?? []).includes('crm.write');
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title={t('crm.title')}
        count={
          customers.length > 0
            ? query.hasNextPage
              ? t('tickets.countMore', { count: customers.length })
              : t('tickets.count', { count: customers.length })
            : undefined
        }
        subtitle={t('crm.subtitle')}
        action={
          <div className="row">
            {canWrite ? (
              <Link to="/app/crm/import" className="nav__item">
                {t('crm.import')}
              </Link>
            ) : null}
            {canWrite ? (
              <Button
                variant="primary"
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                {t('crm.newCustomer')}
              </Button>
            ) : null}
          </div>
        }
      />

      <form
        className="toolbar"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <input
          type="search"
          className="field__control"
          value={searchInput}
          aria-label={t('crm.searchLabel')}
          placeholder={t('crm.searchPlaceholder')}
          onChange={(event) => {
            setSearchInput(event.target.value);
          }}
        />
        <Button type="submit">{t('app.search')}</Button>
      </form>

      <div className="toolbar" role="group" aria-label={t('crm.status')}>
        <Button
          variant={status === undefined ? 'secondary' : 'ghost'}
          aria-pressed={status === undefined}
          onClick={() => {
            setStatus(undefined);
          }}
        >
          {t('tickets.filterAll')}
        </Button>
        {STATUSES.map((value) => (
          <Button
            key={value}
            variant={status === value ? 'secondary' : 'ghost'}
            aria-pressed={status === value}
            onClick={() => {
              setStatus(value);
            }}
          >
            {t(`customerStatus.${value}`)}
          </Button>
        ))}

        <span className="shell__spacer" />

        <Button
          variant={onlyVip ? 'secondary' : 'ghost'}
          aria-pressed={onlyVip}
          onClick={() => {
            setOnlyVip((value) => !value);
          }}
        >
          {t('crm.onlyVip')}
        </Button>

        <DensityToggle />
      </div>

      {query.isError ? <ErrorNotice error={query.error} /> : null}

      <div className="card card--flush" aria-busy={query.isPending}>
        {query.isPending ? <SkeletonTable columns={4} /> : null}

        {!query.isPending && customers.length === 0 ? (
          <EmptyState
            icon="crm"
            title={t('crm.empty')}
            hint={t('crm.emptyHint')}
            action={
              canWrite ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialogOpen(true);
                  }}
                >
                  {t('crm.newCustomer')}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {customers.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('crm.customerNumber')}</th>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('crm.status')}</th>
                  <th scope="col">{t('crm.industry')}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    data-clickable=""
                    onClick={(event) => {
                      // Siehe Ticketliste: Die Zeile fuehrt zur Akte, aber
                      // nur dort, wo nicht schon ein Verweis darunterliegt --
                      // und nicht, wenn gerade Text markiert wird.
                      if ((event.target as HTMLElement).closest('a, button, input, label')) return;
                      if ((window.getSelection()?.toString().length ?? 0) > 0) return;
                      void navigate({
                        to: '/app/crm/$customerId',
                        params: { customerId: customer.id },
                      });
                    }}
                  >
                    <td className="table__number">
                      <Link to="/app/crm/$customerId" params={{ customerId: customer.id }}>
                        {customer.customer_number}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to="/app/crm/$customerId"
                        params={{ customerId: customer.id }}
                        className="table__subject"
                      >
                        {customer.name}
                      </Link>
                      {customer.is_vip ? (
                        <>
                          {' '}
                          <Badge tone="warning" dot>
                            {t('crm.vip')}
                          </Badge>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Badge tone={customer.status === 'active' ? 'success' : 'neutral'}>
                        {t(`customerStatus.${customer.status}`)}
                      </Badge>
                    </td>
                    <td className="table__muted">{customer.industry ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {query.hasNextPage ? (
        <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--space-4)' }}>
          <Button
            busy={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            {t('tickets.loadMore')}
          </Button>
        </div>
      ) : null}

      <CustomerDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
